/**
 * Service Worker Registration & Update Manager
 *
 * Handles:
 * - SW registration on app load
 * - Detecting when a new SW is available (new deployment)
 * - Forcing the new SW to activate immediately
 * - Reloading the page to pick up fresh code
 * - Periodic update checks (every 5 min + on visibility change)
 *
 * iOS PWA specifics:
 * - iOS only checks for SW updates on cold launch, not on resume
 * - We force an update check on visibilitychange as a workaround
 * - navigator.serviceWorker.getRegistration() works on iOS 16.4+
 */

const SW_URL = '/sw.js';
const UPDATE_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

let refreshing = false;

export function register() {
  if (!('serviceWorker' in navigator)) {
    console.debug('[SW] Service workers not supported');
    return;
  }

  // Register when the window loads
  window.addEventListener('load', () => {
    // Clear the reload guard once the page has fully loaded after a SW-triggered refresh
    sessionStorage.removeItem('sw_reloaded');
    registerSW();
  });
}

async function registerSW() {
  try {
    const registration = await navigator.serviceWorker.register(SW_URL, {
      // Do not set updateViaCache — let the browser default (which honours HTTP cache headers)
      // Azure SWA serves sw.js with no-cache so the browser always re-fetches it
    });

    console.debug('[SW] Registered:', registration.scope);

    // Only check for updates if we already have an active SW (skip on first install)
    if (registration.active) {
      registration.update().catch(() => {});
    }

    // Listen for new service worker installing
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (!newWorker) return;

      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed') {
          if (registration.active) {
            // New SW installed while old one is still active → new version available
            console.debug('[SW] New version installed, activating...');
            // Tell the new SW to take over immediately
            newWorker.postMessage('SKIP_WAITING');
          }
        }
      });
    });

    // When a new SW takes control, reload to get fresh code
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      // Persist the guard across the reload so we don't loop
      if (sessionStorage.getItem('sw_reloaded')) {
        console.debug('[SW] Skipping reload — already reloaded for this update');
        return;
      }
      refreshing = true;
      sessionStorage.setItem('sw_reloaded', '1');
      console.debug('[SW] New version active, reloading...');
      window.location.reload();
    });

    // Periodic update checks (only if there's already an active SW)
    if (registration.active) {
      setInterval(() => {
        registration.update().catch(() => {});
      }, UPDATE_CHECK_INTERVAL);
    }

    // Check for updates when the app becomes visible (critical for iOS PWA)
    // iOS doesn't reliably check for SW updates on resume, so we force it
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && registration.active) {
        registration.update().catch(() => {});
      }
    });

    // Also check when the PWA is focused (covers iOS scenarios)
    window.addEventListener('focus', () => {
      if (registration.active) {
        registration.update().catch(() => {});
      }
    });

  } catch (error) {
    console.error('[SW] Registration failed:', error);
  }
}

/**
 * Unregister all service workers and clear all caches.
 * Useful for debugging or forced reset.
 */
export async function unregister() {
  if (!('serviceWorker' in navigator)) return;

  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((r) => r.unregister()));

  const keys = await caches.keys();
  await Promise.all(keys.map((k) => caches.delete(k)));

  console.debug('[SW] Unregistered and caches cleared');
}
